import {
  faBell,
  faFileText,
  faLanguage,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useCaseTranslateComment } from 'api/case/case';
import { ContextMenuOption } from 'components/ContextMenu';
import EllipsisContextMenu from 'components/ContextMenu/EllipsisContextMenu';
import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import styled, { useTheme } from 'styled-components';
import { EventSection } from './components';
import DefaultEventHeader from './DefaultEventHeader';
import useMe from 'contexts/authentication/useMe';
import Roles from 'constants/Roles';
import RichMessage from 'components/RichMessage';
import useGlobalSettings from 'hooks/useGlobalSettings';
import { EventModel, GlobalSettingEnum, TranslationLookups } from 'api/model';

const parentHoverButton = 'parentHoverButton';
const MyEventSection = styled(EventSection)`
  &:hover .${parentHoverButton}, ${`.${parentHoverButton}`}:focus-within {
    background: ${({ theme }) => theme.colors.background.button};
    color: ${({ theme }) => theme.colors.foreground.button};
  }
`;

const HeaderTextSpan = styled.span`
  display: flex;
  align-items: flex-start;
  width: 100%;
  gap: 5px;
`;

const Horiz = styled.div`
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  padding-top: 5px;

  & > *:last-child {
    align-self: flex-end;
  }
`;

const TranslationToggle = styled.button`
  background: none;
  border: none;
  color: ${({ theme }) => theme.colors.foreground.tint};
  cursor: pointer;
  font-size: 0.85em;
  padding: 4px 0;
  margin-top: 4px;
  display: flex;
  align-items: center;
  gap: 4px;

  &:hover {
    text-decoration: underline;
  }
`;

const MessageContainer = styled.div`
  flex: 1;
`;

const ReasonsRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 6px;
`;

const ReasonChip = styled.span`
  padding: 2px 8px;
  border-radius: 999px;
  border: 1px solid ${({ theme }) => theme.colors.border.primary};
  background: ${({ theme }) => theme.colors.background.secondary};
  font-size: 0.85em;
`;

interface Props {
  event: EventModel;
  translations: TranslationLookups;
}

const DeviationReportEvent: FC<Props> = ({ event, translations }) => {
  const theme = useTheme();
  const me = useMe();
  const { parsedData: globalSettings } = useGlobalSettings();
  const hasTranslationAccess =
    globalSettings[GlobalSettingEnum.UsersWithAccessToTranslation]?.[
      me?.userID ?? -1
    ] === true;
  const [translatedText, setTranslatedText] = useState<string | null>(null);
  const [showTranslated, setShowTranslated] = useState(false);

  const translateMutation = useCaseTranslateComment();

  const { caseEventTypes } = translations;

  // Load cached translation from event data on mount
  useEffect(() => {
    const cachedTranslation = event.translations?.Swedish;
    if (cachedTranslation) {
      setTranslatedText(cachedTranslation);
    }
  }, [event]);

  const handleTranslateClick = useCallback(async () => {
    if (translatedText) {
      setShowTranslated(true);
      return;
    }

    try {
      const result = await translateMutation.mutateAsync({
        params: {
          eventId: event.id,
          targetLanguage: 'Swedish',
        },
      });
      setTranslatedText(result.translatedText);
      setShowTranslated(true);
    } catch {
      // translation failed silently — user can retry
    }
  }, [event.id, translatedText, translateMutation]);

  const menuOptions = useMemo((): ContextMenuOption[] => {
    const opts: ContextMenuOption[] = [];

    if (hasTranslationAccess && !translatedText && event.message) {
      opts.push({
        icon: <FontAwesomeIcon icon={faLanguage} />,
        label: translateMutation.isLoading ? 'Översätter...' : 'Översätt',
        onClick: handleTranslateClick,
      });
    }

    return opts;
  }, [hasTranslationAccess, translatedText, event.message, translateMutation.isLoading, handleTranslateClick]);

  return (
    <MyEventSection>
      <DefaultEventHeader
        position={event.position}
        created={event.created}
        headerText={
          <HeaderTextSpan>
            <FontAwesomeIcon
              color={
                event.readByAdmin
                  ? theme.colors.foreground.tint
                  : theme.colors.foreground.error
              }
              icon={faFileText}
            />
            <span>{caseEventTypes[event.caseEventType]}</span>
            {!event.readByAdmin && (
              <span>
                <FontAwesomeIcon
                  color={theme.colors.foreground.newMessage}
                  icon={faBell}
                />
              </span>
            )}
          </HeaderTextSpan>
        }
        modified={event.modified}
        modifiedByID={event.modifiedByID}
        createdByID={event.createdByID}
        userName={event.createdBy}
      />

      <Horiz>
        <MessageContainer>
          <RichMessage text={hasTranslationAccess && showTranslated && translatedText ? translatedText : event.message} />
          {(event.deviationReasons?.length ?? 0) > 0 && (
            <ReasonsRow>
              {event.deviationReasons!.map((reason) => (
                <ReasonChip key={reason}>
                  {translations.deviationReasons?.[reason] ?? reason}
                </ReasonChip>
              ))}
            </ReasonsRow>
          )}
          {hasTranslationAccess && translatedText && (
            <TranslationToggle onClick={() => setShowTranslated(!showTranslated)}>
              <FontAwesomeIcon icon={faLanguage} />
              {showTranslated ? 'Visa original' : 'Visa översättning'}
            </TranslationToggle>
          )}
        </MessageContainer>
        {me?.roles.includes(Roles.Admin) && menuOptions.length > 0 && (
          <EllipsisContextMenu
            className={parentHoverButton}
            options={menuOptions}
            position="right"
          />
        )}
      </Horiz>
    </MyEventSection>
  );
};

export default DeviationReportEvent;
